// System-wide permission definitions
// These define all available permissions in the system
// Permissions marked as superUserOnly can only be assigned by super users

export const PERMISSIONS = [
  // Runbook Management Permissions (Client-level)
  {
    permissionKey: "runbooks.create",
    permissionName: "Create Runbooks",
    permissionDescription: "Create new runbooks",
    category: "Runbook Management",
    superUserOnly: false,
  },
  {
    permissionKey: "runbooks.edit",
    permissionName: "Edit Runbooks",
    permissionDescription: "Edit existing runbooks",
    category: "Runbook Management",
    superUserOnly: false,
  },
  {
    permissionKey: "runbooks.delete",
    permissionName: "Delete Runbooks",
    permissionDescription: "Delete runbooks",
    category: "Runbook Management",
    superUserOnly: false,
  },
  {
    permissionKey: "runbooks.execute",
    permissionName: "Execute Runbooks",
    permissionDescription: "Execute runbooks manually",
    category: "Runbook Management",
    superUserOnly: false,
  },
  {
    permissionKey: "runbooks.validate",
    permissionName: "Validate Runbooks",
    permissionDescription: "Validate runbook configuration",
    category: "Runbook Management",
    superUserOnly: false,
  },
  {
    permissionKey: "runbooks.favorite",
    permissionName: "Favorite Runbooks",
    permissionDescription: "Add and remove favorite runbooks",
    category: "Runbook Management",
    superUserOnly: false,
  },

  // Runbook Version Management Permissions (Client-level)
  {
    permissionKey: "runbook-versions.view",
    permissionName: "View Runbook Versions",
    permissionDescription: "View runbook version history and details",
    category: "Runbook Management",
    superUserOnly: false,
  },
  {
    permissionKey: "runbook-versions.edit",
    permissionName: "Edit/Create Runbook Versions",
    permissionDescription: "Create and edit runbook versions",
    category: "Runbook Management",
    superUserOnly: false,
  },
  {
    permissionKey: "runbook-versions.lock",
    permissionName: "Lock Runbook Versions",
    permissionDescription: "Lock runbook versions to prevent changes",
    category: "Runbook Management",
    superUserOnly: false,
  },
  {
    permissionKey: "runbook-versions.publish",
    permissionName: "Publish Runbook Versions",
    permissionDescription: "Publish runbook versions",
    category: "Runbook Management",
    superUserOnly: false,
  },
  {
    permissionKey: "runbook-versions.activate",
    permissionName: "Activate Runbook Versions",
    permissionDescription: "Activate runbook versions for execution",
    category: "Runbook Management",
    superUserOnly: false,
  },

  // Connector Management Permissions (Client-level)
  {
    permissionKey: "connectors.view",
    permissionName: "View Connectors",
    permissionDescription: "View connector configurations",
    category: "Connector Management",
    superUserOnly: false,
  },
  {
    permissionKey: "connectors.edit",
    permissionName: "Edit Connectors",
    permissionDescription: "Edit existing connectors",
    category: "Connector Management",
    superUserOnly: false,
  },
  {
    permissionKey: "connectors.editSecrets",
    permissionName: "Edit Connector Secrets",
    permissionDescription: "Edit sensitive connector credentials and secrets",
    category: "Connector Management",
    superUserOnly: false,
  },
  {
    permissionKey: "connectors.delete",
    permissionName: "Delete Connectors",
    permissionDescription: "Delete connector configurations",
    category: "Connector Management",
    superUserOnly: false,
  },

  // Schedule Management Permissions (Client-level)
  {
    permissionKey: "schedules.view",
    permissionName: "View Schedules",
    permissionDescription: "View scheduled runbooks",
    category: "Schedule Management",
    superUserOnly: false,
  },
  {
    permissionKey: "schedules.create",
    permissionName: "Create Schedules",
    permissionDescription: "Create runbook schedules",
    category: "Schedule Management",
    superUserOnly: false,
  },
  {
    permissionKey: "schedules.edit",
    permissionName: "Edit Schedules",
    permissionDescription: "Edit runbook schedules",
    category: "Schedule Management",
    superUserOnly: false,
  },
  {
    permissionKey: "schedules.delete",
    permissionName: "Delete Schedules",
    permissionDescription: "Delete runbook schedules",
    category: "Schedule Management",
    superUserOnly: false,
  },

  // Variable Management Permissions (Client-level)
  {
    permissionKey: "variables.view",
    permissionName: "View Variables",
    permissionDescription: "View client variables",
    category: "Variable Management",
    superUserOnly: false,
  },
  {
    permissionKey: "variables.create",
    permissionName: "Create Variables",
    permissionDescription: "Create client variables",
    category: "Variable Management",
    superUserOnly: false,
  },
  {
    permissionKey: "variables.edit",
    permissionName: "Edit Variables",
    permissionDescription: "Edit client variables",
    category: "Variable Management",
    superUserOnly: false,
  },
  {
    permissionKey: "variables.delete",
    permissionName: "Delete Variables",
    permissionDescription: "Delete client variables",
    category: "Variable Management",
    superUserOnly: false,
  },

  {
    permissionKey: "history.view",
    permissionName: "View Execution History",
    permissionDescription: "View runbook execution history",
    category: "Runbook Management",
    superUserOnly: false,
  },

  // Client Management Permissions (Super User Only)
  {
    permissionKey: "clients.view",
    permissionName: "View Clients",
    permissionDescription: "View all clients in the system",
    category: "Client Management",
    superUserOnly: true,
  },
  {
    permissionKey: "clients.create",
    permissionName: "Create Clients",
    permissionDescription: "Create new client organizations",
    category: "Client Management",
    superUserOnly: true,
  },
  {
    permissionKey: "clients.edit",
    permissionName: "Edit Clients",
    permissionDescription: "Edit client organizations",
    category: "Client Management",
    superUserOnly: true,
  },
  {
    permissionKey: "clients.delete",
    permissionName: "Delete Clients",
    permissionDescription: "Delete client organizations",
    category: "Client Management",
    superUserOnly: true,
  },

  // System Administration Permissions (Super User Only)
  {
    permissionKey: "broadcast.manage",
    permissionName: "Manage Broadcasts",
    permissionDescription: "Create and manage system-wide broadcast messages",
    category: "System Administration",
    superUserOnly: true,
  },
  {
    permissionKey: "permissions.manage",
    permissionName: "Manage Permissions",
    permissionDescription: "Manage permissions, groups, and user assignments",
    category: "System Administration",
    superUserOnly: true,
  },
  {
    permissionKey: "system.configure",
    permissionName: "System Configuration",
    permissionDescription: "Configure system-wide settings and preferences",
    category: "System Administration",
    superUserOnly: true,
  },
  {
    permissionKey: "audit.view",
    permissionName: "View Audit Logs",
    permissionDescription: "View system audit logs and activity",
    category: "System Administration",
    superUserOnly: true,
  },
  {
    permissionKey: "servers.manage",
    permissionName: "Manage Runbook Servers",
    permissionDescription: "Configure and manage runbook execution servers",
    category: "System Administration",
    superUserOnly: true,
  },
];

// Helper function to get permissions by category
export const getPermissionsByCategory = () => {
  const categories = {};
  PERMISSIONS.forEach((permission) => {
    if (!categories[permission.category]) {
      categories[permission.category] = [];
    }
    categories[permission.category].push(permission);
  });
  return categories;
};

// Helper function to get client-level permissions only
export const getClientPermissions = () => {
  return PERMISSIONS.filter((p) => !p.superUserOnly);
};

// Helper function to get super user permissions only
export const getSuperUserPermissions = () => {
  return PERMISSIONS.filter((p) => p.superUserOnly);
};

// Helper function to find a permission by key
export const getPermissionByKey = (key) => {
  return PERMISSIONS.find((p) => p.permissionKey === key);
};
